import validateCreditCard from "@insite/client-framework/Common/Utilities/validateCreditCard";
import logger from "@insite/client-framework/Logger";
import { TokenExConfig } from "@insite/client-framework/Services/SettingsService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadTokenExConfig from "@insite/client-framework/Store/Context/Handlers/LoadTokenExConfig";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { generateTokenExFrameStyleConfig } from "@insite/mobius/TokenExFrame";
import { EventEx } from "@preferredMedical/Models/Misc/EventEx";
import { CustomState } from "@preferredMedical/Store/CustomState";
import CreditCardDetailsEntry, {
    CreditCardDetailsEntryStyles,
} from "@preferredMedical/Widgets/SecureOnlineBillPaymentForm/CreditCardDetailsEntry";
import React, { Component, RefObject } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

export interface SecureOnlineBillPaymentFormStyles {
    creditCardDetailsGridItem?: GridItemProps;
    creditCardDetails?: CreditCardDetailsEntryStyles;
}

const secureOnlineBillPaymentFormStyles: SecureOnlineBillPaymentFormStyles = {
    creditCardDetailsGridItem: {
        width: 12,
        css: css`
            padding-left: 10px;
        `,
    },
};

// TokenEx doesn't provide an npm package or type definitions for using the iframe solution.
// This is just enough types to avoid the build warnings and make using TokenEx a bit easier.
export type TokenExIframeStyles = {
    base?: string;
    focus?: string;
    error?: string;
    cvv?: TokenExIframeStyles;
};

export type TokenExIframeConfig = {
    tokenExID: string;
    tokenScheme: string;
    authenticationKey: string;
    timestamp: string;
    origin: string;
    styles?: TokenExIframeStyles;
    pci?: boolean;
    enableValidateOnBlur?: boolean;
    inputType?: "number" | "tel" | "text";
    debug?: boolean;
    cvv?: boolean;
    cvvOnly?: boolean;
    cvvContainerID?: string;
    cvvInputType?: "number" | "tel" | "text";
    enableAriaRequired?: boolean;
    customDataLabel?: string;
    title?: string;
};

type TokenExPCIIframeConfig = TokenExIframeConfig & {
    pci: true;
    enablePrettyFormat?: boolean;
};

export type IFrame = {
    new (containerId: string, config: TokenExIframeConfig): IFrame;
    load: () => void;
    on: (
        eventName: "load" | "validate" | "tokenize" | "error" | "cardTypeChange",
        callback: (data: any) => void,
    ) => void;
    remove: () => void;
    validate: () => void;
    tokenize: () => void;
    reset: () => void;
};

export type TokenEx = {
    Iframe: IFrame;
};

declare const TokenEx: TokenEx;
let tokenExIframe: IFrame | undefined;

let tokenExFrameStyleConfig: TokenExIframeStyles;

interface OwnProps {
    initialData: CardData;
    settings: CardHelperSettings;
    triggerValidateEvent: EventEx;
    getDataEvent: EventEx;
    submitEvent: EventEx;
    resetTokenExEvent: EventEx;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

const mapStateToProps = (state: CustomState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const settingsCollection = getSettingsCollection(state);
    let paymentOptions;
    if (cartState.value && cartState.value.paymentOptions) {
        paymentOptions = cartState.value.paymentOptions;
    }
    let cardTypes;
    if (paymentOptions && paymentOptions.cardTypes) {
        cardTypes = paymentOptions.cardTypes;
    }
    return {
        styles: secureOnlineBillPaymentFormStyles,
        paymentOptions,
        cardTypes,
        state,
        settingsCollection,
    };
};

const mapDispatchToProps = {
    loadTokenExConfig,
};

interface State {
    cardData: CardData;
    cardErrors: CardErrors;
    refs: Refs;
    saveCard: boolean;
    isTokenExIframeLoaded: boolean;
    isCardNumberTokenized: boolean;
    showFormErrors: boolean;
}

export interface CardHelperSettings {
    canSaveCard: boolean;
    saveCard: boolean;
}

export interface CardData {
    possibleCardType: string;
    cardHolderName: string;
    cardNumber: string;
    cardType: string;
    expirationMonth: number;
    expirationYear: number;
    securityCode: string;
}

interface CardErrors {
    cardHolderName: string;
    cardNumber: string;
    cardType: string;
    expiration: string;
    securityCode: string;
}

interface Refs {
    cardHolderName: RefObject<HTMLInputElement>;
    cardNumber: RefObject<HTMLInputElement>;
    cardType: RefObject<HTMLSelectElement>;
    expirationMonth: RefObject<HTMLSelectElement>;
    expirationYear: RefObject<HTMLSelectElement>;
    securityCode: RefObject<HTMLInputElement>;
}

const isNonEmptyString = (value: string | undefined) => value !== undefined && value.trim() !== "";

const isMonthAndYearBeforeToday = (month: number, year: number) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    return year < currentYear || (year === currentYear && month < currentMonth + 1);
};

const convertTokenExCardTypeToApiData = (cardType: string) => {
    if (cardType.includes("american")) {
        return "AMERICAN EXPRESS";
    }
    return cardType.toUpperCase();
};

const generateMonths = () => {
    const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];
    const result = [];
    for (let i = 0; i < months.length; i++) {
        result.push({ key: months[i], value: i + 1 });
    }
    return result;
};

const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const result = [];
    for (let i = 0; i < 12; i++) {
        result.push({ key: currentYear + i, value: currentYear + i });
    }
    return result;
};

class CardHelper extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            cardData: {
                possibleCardType: "",
                cardHolderName: this.props.initialData.cardHolderName,
                cardNumber: this.props.initialData.cardNumber,
                cardType: this.props.initialData.cardType,
                expirationMonth: this.props.initialData.expirationMonth,
                expirationYear: this.props.initialData.expirationYear,
                securityCode: this.props.initialData.securityCode,
            },
            cardErrors: {
                cardHolderName: "",
                cardNumber: "",
                cardType: "",
                expiration: "",
                securityCode: "",
            },
            refs: {
                cardHolderName: React.createRef(),
                cardNumber: React.createRef(),
                cardType: React.createRef(),
                expirationMonth: React.createRef(),
                expirationYear: React.createRef(),
                securityCode: React.createRef(),
            },
            saveCard: false,
            isTokenExIframeLoaded: false,
            isCardNumberTokenized: false,
            showFormErrors: false,
        };

        if (!tokenExFrameStyleConfig) {
            tokenExFrameStyleConfig = generateTokenExFrameStyleConfig({ theme: this.props.theme });
        }
        this.setUpTokenExIFrame = this.setUpTokenExIFrame.bind(this);
        this.loadTokenExConfigWithRetry = this.loadTokenExConfigWithRetry.bind(this);
        this.handleSaveCardChange = this.handleSaveCardChange.bind(this);
        this.handleCardHolderNameChange = this.handleCardHolderNameChange.bind(this);
        this.handleCardNumberChange = this.handleCardNumberChange.bind(this);
        this.handleCardTypeChange = this.handleCardTypeChange.bind(this);
        this.handleExpirationMonthChange = this.handleExpirationMonthChange.bind(this);
        this.handleExpirationYearChange = this.handleExpirationYearChange.bind(this);
        this.handleSecurityCodeChange = this.handleSecurityCodeChange.bind(this);
        this.validateCardHolderName = this.validateCardHolderName.bind(this);
        this.validateCardNumber = this.validateCardNumber.bind(this);
        this.validateCardType = this.validateCardType.bind(this);
        this.validateCardExpiration = this.validateCardExpiration.bind(this);
        this.validateSecurityCode = this.validateSecurityCode.bind(this);
    }

    componentDidMount() {
        const script = document.createElement("script");
        script.src = this.props.settingsCollection.websiteSettings.tokenExTestMode
            ? "https://test-htp.tokenex.com/Iframe/Iframe-v3.min.js"
            : "https://htp.tokenex.com/Iframe/Iframe-v3.min.js";
        script.async = true;
    
        script.onload = () => {
            this.loadTokenExConfigWithRetry();
        };
    
        document.body.appendChild(script);
    
        this.props.loadTokenExConfig({ token: "", isECheck: false });
        this.props.submitEvent.subscribe(() => {
            if (tokenExIframe) {
                tokenExIframe.tokenize();
            }
        });
        this.props.triggerValidateEvent.subscribe(async () => {
            await this.validateCardHolderName(this.state.cardData.cardHolderName);
            await this.validateCardNumber(this.state.cardData.cardNumber);
            await this.validateCardExpiration(this.state.cardData.expirationMonth, this.state.cardData.expirationYear);
            await this.validateCardType(this.state.cardData.cardType);
            await this.validateSecurityCode(this.state.cardData.securityCode);
            if (tokenExIframe) {
                tokenExIframe.validate();
            }
        });
        this.props.resetTokenExEvent.subscribe(() => {
            if (tokenExIframe) {
                tokenExIframe.reset();
            }
        });
    }    

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (
            Object.keys(prevProps.state.context.tokenExConfigs).length !==
            Object.keys(this.props.state.context.tokenExConfigs).length
        ) {
            this.setUpTokenExIFrame(this.props.state.context.tokenExConfigs[""]);
            return;
        }
        let allTokenExConfigsIdentical = true;
        const currentConfigs = Object.keys(this.props.state.context.tokenExConfigs);
        const prevConfigs = Object.keys(prevProps.state.context.tokenExConfigs);
        for (let i = 0; i < currentConfigs.length; i++) {
            const currentConfig = this.props.state.context.tokenExConfigs[currentConfigs[i]];
            const prevConfig = prevProps.state.context.tokenExConfigs[prevConfigs[i]];
            if (currentConfig.authenticationKey !== prevConfig.authenticationKey) {
                allTokenExConfigsIdentical = false;
                break;
            }
        }
        if (!allTokenExConfigsIdentical) {
            this.setUpTokenExIFrame(this.props.state.context.tokenExConfigs[""]);
        }
    }
    
    loadTokenExConfigWithRetry(retries = 10, delay = 1000) {
        const config = this.props.state.context.tokenExConfigs[""];
        if (config) {
            this.setUpTokenExIFrame(config);
        } else if (retries > 0) {
            setTimeout(() => {
                this.loadTokenExConfigWithRetry(retries - 1, delay);
            }, delay);
        } else {
            logger.error("TokenExConfig is undefined after multiple retries");
        }
    }

    handleSaveCardChange(event: React.SyntheticEvent, value: boolean) {
        this.setState({ saveCard: value });
    }

    handleCardHolderNameChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState({ cardData: { ...this.state.cardData, cardHolderName: event.currentTarget.value } });
        this.validateCardHolderName(event.currentTarget.value);
    }

    handleCardNumberChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState({ cardData: { ...this.state.cardData, cardNumber: event.currentTarget.value } });
        this.validateCardNumber(event.currentTarget.value);
    }
    handleCardTypeChange(event: React.ChangeEvent<HTMLSelectElement>) {
        this.setState({ cardData: { ...this.state.cardData, cardType: event.currentTarget.value } });
        this.validateCardType(event.currentTarget.value);
    }
    handleExpirationMonthChange(event: React.ChangeEvent<HTMLSelectElement>) {
        const month = Number(event.currentTarget.value);
        if (Number.isNaN(month)) {
            return;
        }
        this.setState({ cardData: { ...this.state.cardData, expirationMonth: month } });
        this.validateCardExpiration(month, this.state.cardData.expirationYear);
    }
    handleExpirationYearChange(event: React.ChangeEvent<HTMLSelectElement>) {
        const year = Number(event.currentTarget.value);
        if (Number.isNaN(year)) {
            return;
        }
        this.setState({ cardData: { ...this.state.cardData, expirationYear: year } });
        this.validateCardExpiration(this.state.cardData.expirationMonth, year);
    }
    handleSecurityCodeChange(event: React.ChangeEvent<HTMLInputElement>) {
        this.setState({ cardData: { ...this.state.cardData, securityCode: event.currentTarget.value } });
        this.validateSecurityCode(event.currentTarget.value);
    }

    async validateCardHolderName(cardHolderName: string) {
        const cardHolderNameValid = isNonEmptyString(cardHolderName);
        await this.setState({
            cardErrors: {
                ...this.state.cardErrors,
                cardHolderName: !cardHolderNameValid ? translate("Cardholder name is required.") : "",
            },
        });
        return cardHolderNameValid;
    }

    async validateCardNumber(cardNumber: string) {
        if (this.state.isCardNumberTokenized) {
            return true;
        }

        const cardNumberEmpty = !isNonEmptyString(cardNumber);
        const cardNumberValid = validateCreditCard(cardNumber);

        if (cardNumberEmpty) {
            await this.setState({
                cardErrors: { ...this.state.cardErrors, cardNumber: translate("Credit card number is required.") },
            });
        } else if (!cardNumberValid) {
            await this.setState({
                cardErrors: { ...this.state.cardErrors, cardNumber: translate("Credit card number is invalid.") },
            });
        } else {
            await this.setState({ cardErrors: { ...this.state.cardErrors, cardNumber: "" } });
        }

        return { cardNumberEmpty, cardNumberValid };
    }

    async validateCardType(cardType: string) {
        const cardTypeValid = isNonEmptyString(cardType);
        await this.setState({
            cardErrors: {
                ...this.state.cardErrors,
                cardType: !cardTypeValid ? translate("Credit card type is required.") : "",
            },
        });
        return cardTypeValid;
    }

    async validateCardExpiration(expirationMonth: number, expirationYear: number) {
        const cardExpired = isMonthAndYearBeforeToday(expirationMonth, expirationYear);
        await this.setState({
            cardErrors: {
                ...this.state.cardErrors,
                expiration: cardExpired ? translate("Card is expired. Please enter a valid expiration date.") : "",
            },
        });
        return cardExpired;
    }

    async validateSecurityCode(securityCode: string) {
        const securityCodeEmpty = !isNonEmptyString(securityCode);
        const securityCodeValid = /^\d+$/.test(securityCode);

        if (securityCodeEmpty) {
            await this.setState({
                cardErrors: { ...this.state.cardErrors, securityCode: translate("Security code is required.") },
            });
        } else if (!securityCodeValid) {
            await this.setState({
                cardErrors: { ...this.state.cardErrors, securityCode: translate("Security code is invalid.") },
            });
        } else {
            await this.setState({ cardErrors: { ...this.state.cardErrors, securityCode: "" } });
        }

        return { securityCodeEmpty, securityCodeValid };
    }

    setUpTokenExIFrame(config: TokenExConfig) {
        if (typeof TokenEx === "undefined") {
            setTimeout(() => {
                this.setUpTokenExIFrame(config);
            }, 100);
            return;
        }

        if (tokenExIframe) {
            tokenExIframe.remove();
        }

        const iframeConfig: TokenExPCIIframeConfig = {
            authenticationKey: config.authenticationKey,
            cvv: true,
            cvvContainerID: "tokenExSecurityCode",
            cvvInputType: "text",
            enablePrettyFormat: true,
            inputType: "text",
            origin: config.origin,
            pci: true,
            styles: tokenExFrameStyleConfig,
            timestamp: config.timestamp,
            tokenExID: config.tokenExId,
            tokenScheme: config.tokenScheme,
            enableAriaRequired: true,
            title: translate("Credit card information"),
            customDataLabel: translate("credit card number"),
        };

        tokenExIframe = new TokenEx.Iframe("tokenExCardNumber", iframeConfig);
        tokenExIframe.load();
        tokenExIframe.on("load", () => {
            this.setState({ isTokenExIframeLoaded: true });
        });
        tokenExIframe.on("tokenize", data => {
            const cardData = {
                cardHolderName: this.state.cardData.cardHolderName,
                cardNumber: data.token,
                cardType: convertTokenExCardTypeToApiData(data.cardType),
                expirationMonth: this.state.cardData.expirationMonth,
                expirationYear: this.state.cardData.expirationYear,
                securityCode: "CVV",
                possibleCardType: data.possibleCardType,
            };
            this.setState({ cardData, isCardNumberTokenized: true }, () => {
                this.props.getDataEvent.emitAsync(cardData);
            });
        });
        tokenExIframe.on("cardTypeChange", data => {
            this.setState({ cardData: { ...this.state.cardData, possibleCardType: data.possibleCardType } });
        });
        tokenExIframe.on("validate", data => {
            this.setState({ showFormErrors: true });
            if (data.isValid) {
                this.setState({ cardErrors: { ...this.state.cardErrors, cardNumber: "" } });
            } else {
                if (data.validator === "required") {
                    this.setState({
                        cardErrors: {
                            ...this.state.cardErrors,
                            cardNumber: translate("Credit card number is required."),
                        },
                    });
                }
                if (data.validator === "format") {
                    this.setState({
                        cardErrors: {
                            ...this.state.cardErrors,
                            cardNumber: translate("Credit card number is invalid."),
                        },
                    });
                }
            }

            if (data.isCvvValid) {
                this.setState({ cardErrors: { ...this.state.cardErrors, securityCode: "" } });
            } else {
                if (data.cvvValidator === "required") {
                    this.setState({
                        cardErrors: { ...this.state.cardErrors, securityCode: translate("Security code is required.") },
                    });
                }
                if (data.cvvValidator === "format") {
                    this.setState({
                        cardErrors: { ...this.state.cardErrors, securityCode: translate("Security code is invalid.") },
                    });
                }
            }
        });
        tokenExIframe.on("error", data => {
            logger.error(data);
            this.setUpTokenExIFrame(config);
        });
    }

    render() {
        return (
            <GridItem {...this.props.styles.creditCardDetailsGridItem}>
                <CreditCardDetailsEntry
                    canSaveCard={this.props.settings.canSaveCard}
                    iframe="TokenEx"
                    isTokenExIframeLoaded={this.state.isTokenExIframeLoaded}
                    saveCard={this.props.settings.saveCard}
                    onSaveCardChange={this.handleSaveCardChange}
                    cardHolderName={this.state.cardData.cardHolderName}
                    cardHolderNameRef={this.state.refs.cardHolderName}
                    onCardHolderNameChange={this.handleCardHolderNameChange}
                    cardHolderNameError={this.state.showFormErrors ? this.state.cardErrors.cardHolderName : undefined}
                    cardNumber={this.state.cardData.cardNumber}
                    cardNumberRef={this.state.refs.cardNumber}
                    onCardNumberChange={this.handleCardNumberChange}
                    cardNumberError={this.state.showFormErrors ? this.state.cardErrors.cardNumber : undefined}
                    cardType={this.state.cardData.cardType}
                    cardTypeRef={this.state.refs.cardType}
                    possibleCardType={this.state.cardData.possibleCardType}
                    onCardTypeChange={this.handleCardTypeChange}
                    cardTypeError={this.state.showFormErrors ? this.state.cardErrors.cardType : undefined}
                    expirationMonth={this.state.cardData.expirationMonth}
                    expirationMonthRef={this.state.refs.expirationMonth}
                    onExpirationMonthChange={this.handleExpirationMonthChange}
                    expirationYear={this.state.cardData.expirationYear}
                    expirationYearRef={this.state.refs.expirationYear}
                    onExpirationYearChange={this.handleExpirationYearChange}
                    expirationError={this.state.showFormErrors ? this.state.cardErrors.expiration : undefined}
                    securityCode={this.state.cardData.securityCode}
                    securityCodeRef={this.state.refs.securityCode}
                    onSecurityCodeChange={this.handleSecurityCodeChange}
                    securityCodeError={this.state.showFormErrors ? this.state.cardErrors.securityCode : undefined}
                    availableCardTypes={this.props.cardTypes ? this.props.cardTypes : []}
                    availableMonths={generateMonths()}
                    availableYears={generateYears()}
                    extendedStyles={this.props.styles.creditCardDetails}
                />
            </GridItem>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(CardHelper));
